import {GameObjectType, TLevelObject} from "../../config/types.js";
import {AABB} from "../core/AABB.js";
import {Vec2} from "../core/Vec2.js";
import Attractor from "../game/Attractor.js";
import Line from "../game/Line.js";
import Shard from "../game/Shard.js";
import {Player} from "../player/Player.js";
import ChunkManager from "./ChunkManager.js";

/**
 * Manages all static level geometry and provides efficient spatial querying
 * for game objects.
 * This class is a pure data container.
 */
export class World {
  private readonly boundingBox: AABB;
  private readonly spawnPosition: Vec2;
  private readonly chunkManager: ChunkManager<{
    lines: Line[];
    shards: Shard[];
    attractors: Attractor[];
  }>;
  private readonly totalShardCount: number;

  constructor(levelData: TLevelObject[]) {
    const spawn = Vec2.zero();
    const lines: Line[] = [];
    const shards: Shard[] = [];
    const attractors: Attractor[] = [];
    let shardId = 0;

    levelData.forEach((obj) => {
      switch (obj.type) {
        case GameObjectType.PLASTIC:
        case GameObjectType.SAND:
        case GameObjectType.ICE:
        case GameObjectType.FIRE:
          lines.push(Line.create(obj));
          break;
        case GameObjectType.SHARD:
          shards.push(Shard.create(obj, shardId++));
          break;
        case GameObjectType.ATTRACTOR:
          attractors.push(Attractor.create(obj));
          break;
        case GameObjectType.SPAWN:
          shardId++;
          spawn.set(obj.position[0], obj.position[1]);
          break;
      }
    });

    this.chunkManager = new ChunkManager({lines, shards, attractors});
    this.spawnPosition = spawn;
    this.boundingBox = AABB.fromTLevelObjects(levelData);
    this.totalShardCount = shards.length;
  }

  /**
   * Returns the initial spawn position for players in this world.
   */
  public getSpawnPosition(): Vec2 {
    return this.spawnPosition.clone();
  }

  /**
   * Returns the total number of shards present in the level.
   */
  public getShardCount(): number {
    return this.totalShardCount;
  }

  /**
   * Efficiently retrieves only the lines that are near the player's position.
   */
  public getLines(player: Player): Line[] {
    return this.chunkManager.getNearbyObjects(player.currentPosition, "lines");
  }

  /**
   * Efficiently retrieves only the attractors that are near the player's position.
   */
  public getAttractors(player: Player): Attractor[] {
    return this.chunkManager.getNearbyObjects(
      player.currentPosition,
      "attractors",
    );
  }

  /**
   * Efficiently retrieves only the shards that are near the player's position.
   */
  public getShards(player: Player): Shard[] {
    return this.chunkManager.getNearbyObjects(player.currentPosition, "shards");
  }

  /**
   * Checks if a player has moved outside the defined level boundaries.
   * @returns `true` if the player is out of bounds.
   */
  public isPlayerOutOfBounds(player: Player): boolean {
    const margin = 20; // A small buffer zone
    return (
      player.currentPosition.x < this.boundingBox.lowerBound.x - margin ||
      player.currentPosition.x > this.boundingBox.upperBound.x + margin ||
      player.currentPosition.y < this.boundingBox.lowerBound.y - margin
    );
  }
}
